import React from 'react';
import { tagColor, tagBackgroundColor } from '@/utils/tagTheme';

const TAG_PATTERN = /#([a-zA-Z0-9_-]+)/g;

interface InlineTagTextProps {
  text: string;
  /**
   * When true, a tag that sits at the very end of `text` (no trailing space
   * or character behind it) is rendered as plain text. The typing animation
   * uses this so a tag only lights up once its delimiter has been typed.
   */
  excludeTrailingTag?: boolean;
}

/**
 * Renders a string with inline `#tag` tokens highlighted in their hash-derived
 * colors. Used by static landing copy AND by the animated editor.
 */
export function InlineTagText({ text, excludeTrailingTag = false }: InlineTagTextProps) {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  TAG_PATTERN.lastIndex = 0;

  while ((match = TAG_PATTERN.exec(text)) !== null) {
    const matchStart = match.index;
    const matchEnd = matchStart + match[0].length;
    const tagName = match[1].toLowerCase();

    if (matchStart > lastIndex) {
      parts.push(text.substring(lastIndex, matchStart));
    }

    const isAtEnd = matchEnd === text.length;

    if (excludeTrailingTag && isAtEnd) {
      parts.push(text.substring(matchStart, matchEnd));
    } else {
      const isPrecededByText = matchStart > 0 && text[matchStart - 1] !== ' ';
      parts.push(
        <span
          key={`tag-${matchStart}`}
          className="rounded-sm font-medium"
          style={{
            color: tagColor(tagName, 'light'),
            backgroundColor: tagBackgroundColor(tagName, 'light'),
            padding: '0 0.3em',
            marginLeft: isPrecededByText ? '0.25em' : '0',
            display: 'inline',
          }}
        >
          #{tagName}
        </span>,
      );
    }

    lastIndex = matchEnd;
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return <>{parts}</>;
}

export default InlineTagText;
